import * as Yup from "yup";
import { thousandFormat } from "services/helpers";

// Pure logic for the fund valuation report form and list (FactFund replacement).
// Kept out of the components so the rules are unit-tested without rendering.
//
// Amounts travel as strings end to end: the server's DecimalField takes a string, and
// parseFloat would lose digits from a 12-decimal unit value or a 24-digit sum.

export const FUND_VALUATION_TYPES = ["private_investment_fund", "private_investment_fund_unlisted"];

// Server limits (private_investment_funds.FundValuationReport): max_digits 24.
export const FUND_VALUATION_DECIMAL_FIELDS = {
  market_value: { intDigits: 18, decimals: 6, nonNegative: false },
  owned_shares: { intDigits: 18, decimals: 6, nonNegative: true },
  value_per_share: { intDigits: 12, decimals: 12, nonNegative: true },
  fund_size: { intDigits: 18, decimals: 6, nonNegative: true },
  fund_commitment: { intDigits: 18, decimals: 6, nonNegative: true },
  left_commitment: { intDigits: 18, decimals: 6, nonNegative: true },
};

export const FUND_VALUATION_DATE_FIELDS = ["value_date", "report_received", "ifrs_date"];

export const FUND_VALUATION_REPORT_PERIOD_MAX_LENGTH = 32;

export const FUND_VALUATION_ATTACHMENT_EXTENSIONS = ["pdf", "xlsx", "xls", "docx", "doc", "png", "jpg"];
export const FUND_VALUATION_ATTACHMENT_MAX_BYTES = 15 * 1024 * 1024;

// Every form key, including the empty ones, so Formik knows them and a server field
// error shows on a new row too.
export const FUND_VALUATION_DEFAULT_VALUES = {
  id: null,
  portfolio: "",
  value_date: null,
  market_value: "",
  owned_shares: "",
  value_per_share: "",
  fund_size: "",
  fund_commitment: "",
  left_commitment: "",
  report_period: "",
  report_received: null,
  ifrs_date: null,
  attachment: null,
  attachment_name: null,
};

export const FUND_VALUATION_ERROR_FIELDS = Object.keys(FUND_VALUATION_DEFAULT_VALUES).filter(
  (key) => key !== "id" && key !== "attachment_name"
);

const identityT = (key, fallback) => fallback;

// "1 222 938,50" / NBSP / narrow NBSP thousands separators -> "1222938.50".
// Empty or not a number -> null.
export const normalizeDecimal = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  let s = String(value)
    .trim()
    .replace(/[\s\u00a0\u202f]/g, "")
    .replace(/\u2212/g, "-")
    .replace(",", ".");
  if (s === "") {
    return null;
  }
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(s)) {
    return null;
  }
  const negative = s.startsWith("-");
  if (negative) {
    s = s.slice(1);
  }
  if (s.startsWith(".")) {
    s = "0" + s;
  }
  if (s.endsWith(".")) {
    s = s.slice(0, -1);
  }
  return (negative ? "-" : "") + s;
};

const pad2 = (n) => String(n).padStart(2, "0");

// DateField: the local calendar day, never toISOString (a UTC shift would move the day).
export const toDateString = (date) => {
  if (!date) {
    return null;
  }
  if (typeof date === "string") {
    return /^\d{4}-\d{2}-\d{2}/.test(date) ? date.slice(0, 10) : null;
  }
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

// "2026-06-30" -> local Date at midnight (no UTC parsing).
export const parseDate = (value) => {
  if (!value) {
    return null;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) {
    return null;
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

// "12.345678901200" -> "12.3456789012", "1000000.000000" -> "1000000". String only.
export const trimDecimal = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const s = String(value);
  if (!s.includes(".")) {
    return s;
  }
  return s.replace(/0+$/, "").replace(/\.$/, "");
};

const incrementDigits = (digits) => {
  const arr = digits.split("");
  let i = arr.length - 1;
  while (i >= 0) {
    if (arr[i] === "9") {
      arr[i] = "0";
      i -= 1;
    } else {
      arr[i] = String(Number(arr[i]) + 1);
      return arr.join("");
    }
  }
  return "1" + arr.join("");
};

// Two decimals (half up) with thousands separators, string arithmetic only.
export const formatAmount = (value) => {
  const n = normalizeDecimal(value);
  if (n === null) {
    return "";
  }
  const negative = n.startsWith("-");
  const [intPart, fracPart = ""] = (negative ? n.slice(1) : n).split(".");
  const frac = (fracPart + "000").slice(0, 3);
  let digits = intPart + frac.slice(0, 2);
  if (Number(frac[2]) >= 5) {
    digits = incrementDigits(digits);
  }
  const intOut = digits.slice(0, -2).replace(/^0+(?=\d)/, "") || "0";
  const result = `${intOut}.${digits.slice(-2)}`;
  const isZero = /^0\.00$/.test(result);
  return (negative && !isZero ? "-" : "") + thousandFormat(result);
};

// Shares and unit value: trailing zeros off, thousands separators on the integer part.
export const formatPrecise = (value) => {
  const s = trimDecimal(value);
  return s === "" ? "" : thousandFormat(s);
};

const decimalTest = (field, { intDigits, decimals, nonNegative }, t) =>
  Yup.mixed()
    .nullable()
    .test("decimal", t("fund_valuation_report_invalid_number", "Anna luku"), (value) => {
      if (value === null || value === undefined || String(value).trim() === "") {
        return true;
      }
      return normalizeDecimal(value) !== null;
    })
    .test(
      "max_int_digits",
      t("fund_valuation_report_too_many_digits", "Liian suuri luku (enintään {{max}} numeroa ennen desimaalierotinta)", { max: intDigits }),
      (value) => {
        const n = normalizeDecimal(value);
        if (n === null) {
          return true;
        }
        const intPart = n.replace("-", "").split(".")[0].replace(/^0+/, "");
        return intPart.length <= intDigits;
      }
    )
    .test(
      "max_decimals",
      t("fund_valuation_report_too_many_decimals", "Enintään {{max}} desimaalia", { max: decimals }),
      (value) => {
        const n = normalizeDecimal(value);
        if (n === null) {
          return true;
        }
        const frac = n.split(".")[1] || "";
        return frac.length <= decimals;
      }
    )
    .test("non_negative", t("fund_valuation_report_non_negative", "Arvo ei voi olla negatiivinen"), (value) => {
      if (!nonNegative) {
        return true;
      }
      const n = normalizeDecimal(value);
      return n === null || !n.startsWith("-") || /^-0(\.0*)?$/.test(n);
    });

export const buildFundValuationSchema = (required, t = identityT) => {
  const decimals = {};
  Object.entries(FUND_VALUATION_DECIMAL_FIELDS).forEach(([field, rule]) => {
    decimals[field] = decimalTest(field, rule, t);
  });
  return Yup.object().shape({
    portfolio: Yup.mixed().test("required", required, (v) => v !== null && v !== undefined && v !== ""),
    value_date: Yup.mixed().test("required", required, (v) => toDateString(v) !== null),
    ...decimals,
    report_period: Yup.string()
      .nullable()
      .max(
        FUND_VALUATION_REPORT_PERIOD_MAX_LENGTH,
        t("fund_valuation_report_report_period_too_long", "Enintään {{max}} merkkiä", { max: FUND_VALUATION_REPORT_PERIOD_MAX_LENGTH })
      ),
  });
};

// JSON body for POST/PATCH. Read-only fields and the attachment are never in it.
export const toPayload = (values, fundId) => {
  const period = values?.report_period == null ? "" : String(values.report_period).trim();
  const portfolio = values?.portfolio;
  const body = {
    portfolio: portfolio === "" || portfolio == null ? null : Number(portfolio),
    private_investment_fund: fundId,
    report_period: period === "" ? null : period,
  };
  FUND_VALUATION_DATE_FIELDS.forEach((field) => {
    body[field] = toDateString(values?.[field]);
  });
  Object.keys(FUND_VALUATION_DECIMAL_FIELDS).forEach((field) => {
    body[field] = normalizeDecimal(values?.[field]);
  });
  return body;
};

// API row -> form values.
export const fromApi = (row) => {
  const values = { ...FUND_VALUATION_DEFAULT_VALUES };
  if (!row) {
    return values;
  }
  values.id = row.id ?? null;
  values.portfolio = row.portfolio ?? "";
  FUND_VALUATION_DATE_FIELDS.forEach((field) => {
    values[field] = parseDate(row[field]);
  });
  Object.keys(FUND_VALUATION_DECIMAL_FIELDS).forEach((field) => {
    values[field] = trimDecimal(row[field]);
  });
  values.report_period = row.report_period || "";
  values.attachment_name = row.attachment_name || null;
  return values;
};

const asText = (value) => {
  if (Array.isArray(value)) {
    return value.map(asText).join(" ");
  }
  if (value && typeof value === "object") {
    return Object.values(value).map(asText).join(" ");
  }
  return value == null ? "" : String(value);
};

// DRF 400 body -> Formik errors. Field errors go to the field, the attachment's "file"
// error to the attachment field, everything else to "general".
export const buildFundValuationErrors = (data, t = identityT) => {
  const errors = {};
  const general = [];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    Object.entries(data).forEach(([key, value]) => {
      const field = key === "file" ? "attachment" : key;
      if (FUND_VALUATION_ERROR_FIELDS.includes(field)) {
        errors[field] = asText(value);
      } else if (key === "non_field_errors" || key === "detail") {
        general.push(asText(value));
      } else if (key !== "code") {
        general.push(asText(value));
      }
    });
  } else if (data) {
    general.push(asText(data));
  }
  if (general.length > 0) {
    errors.general = general.join(" ");
  }
  if (Object.keys(errors).length === 0) {
    errors.general = t("fund_valuation_report_unknown_error", "Tallennus epäonnistui. Yritä uudelleen.");
  }
  return errors;
};

// Same rule as the server: extension allowlist (case-insensitive), at most 15 MB.
export const validateAttachment = (file, t = identityT) => {
  if (!file) {
    return null;
  }
  const name = String(file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  if (!FUND_VALUATION_ATTACHMENT_EXTENSIONS.includes(ext)) {
    return t("fund_valuation_report_attachment_bad_type", "Sallitut tiedostotyypit: {{types}}", {
      types: FUND_VALUATION_ATTACHMENT_EXTENSIONS.join(", "),
    });
  }
  if (file.size > FUND_VALUATION_ATTACHMENT_MAX_BYTES) {
    return t("fund_valuation_report_attachment_too_large", "Tiedosto on liian suuri (enintään 15 Mt)");
  }
  return null;
};

// The tab: only behind the account's Tableau flag; fund types always, other types only
// when rows already exist (import_factfund matches by symbol, whatever the type).
export const showFundValuationTab = ({ account, fundType, reportCount } = {}) => {
  if (!account?.show_security_dim_fields) {
    return false;
  }
  if (FUND_VALUATION_TYPES.includes(fundType)) {
    return true;
  }
  return Number(reportCount) > 0;
};

// The extra count query is only needed when the type alone does not decide.
export const needsFundValuationCount = ({ account, fundType } = {}) =>
  Boolean(account?.show_security_dim_fields) && !FUND_VALUATION_TYPES.includes(fundType);

// Route decision for the tab: "show", "hidden" (a direct URL redirects to Kurssit) or
// "pending" while the account, the security or the row count is still loading.
export const fundValuationTabState = ({ account, fund, reportCount } = {}) => {
  if (!account || !fund) {
    return "pending";
  }
  if (showFundValuationTab({ account, fundType: fund.type, reportCount })) {
    return "show";
  }
  if (needsFundValuationCount({ account, fundType: fund.type }) && (reportCount === undefined || reportCount === null)) {
    return "pending";
  }
  return "hidden";
};
